import isNil from "lodash/isNil";
import { usePotentialWarningsQueryInvalidate } from "PFApp/booking/components/booking_form/use_potential_warnings/use_potential_warnings_query_invalidate";
import { useRefreshBookingData } from "PFApp/booking/hooks/use_refresh_booking_data";
import { useSyncRows } from "PFApp/booking/parts/overview/use_sync_rows";
import { updateBooking } from "PFApp/booking/services/api";
import { useBookingModuleStoreActions } from "PFApp/booking/store";
import { getGrowlId, useGrowlStoreActions } from "PFApp/stores/growl_store";
import { utc } from "PFCore/helpers/date";
import { useBookingInvalidate } from "PFCore/hooks/queries";
import { useProfileInvalidate } from "PFCore/hooks/queries/profile";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { Booking } from "PFTypes";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

const DRAGGING_BOOKING_SUCCESS_ACTION_ID = getGrowlId("bm-dragging-booking");
const CHART_UPDATE_DELAY_MS = 200;

type BookingDates = {
  startDate: string;
  endDate: string;
};

export const useUpdateBookingDates = () => {
  const { setBookingDragAndDropData } = useBookingModuleStoreActions();
  const { showGrowl } = useGrowlStoreActions();
  const syncRows = useSyncRows();
  const { formatISODateTime } = useDateFormatter();
  const refreshBookingData = useRefreshBookingData();
  const { t } = useTranslation("bookingModule");
  const { invalidateProfileWithBooking } = useProfileInvalidate();
  const { invalidate: invalidateBooking } = useBookingInvalidate();
  const { invalidateAll: invalidatePotentialWarnings } = usePotentialWarningsQueryInvalidate();

  return useCallback(
    async (booking: Booking, { startDate, endDate }: BookingDates): Promise<boolean> => {
      try {
        setBookingDragAndDropData({ isBookingUpdating: true });
        await updateBooking({
          id: booking.id,
          startDate: formatISODateTime(utc(startDate)),
          endDate: formatISODateTime(utc(endDate))
        });
        refreshBookingData({
          bookingIds: [booking.id],
          bookingGroupId: booking.booking_group_id,
          activityId: booking.activity?.id,
          profileIds: booking.profile_id ? [booking.profile_id] : undefined,
          refreshProfile: !isNil(booking.booking_template_id)
        });
        invalidateProfileWithBooking(booking.id);
        invalidateBooking(booking.id);
        invalidatePotentialWarnings();

        const parentActivityIds = booking.activity?.parent_activity?.id
          ? [booking.activity.parent_activity.id]
          : [];
        await syncRows({
          profileId: booking.profile_id,
          activityId: booking.activity?.id,
          parentActivityIds
        });

        await new Promise<void>((resolve) => setTimeout(resolve, CHART_UPDATE_DELAY_MS));
        showGrowl({
          id: DRAGGING_BOOKING_SUCCESS_ACTION_ID,
          kind: "success",
          message: t("bookings.update.success"),
          hidePreviousByTypeAndId: true,
          ttl: 5000
        });
        return true;
      } catch {
        showGrowl({ kind: "error", message: t("bookings.update.error") });
        return false;
      } finally {
        setBookingDragAndDropData({ isBookingUpdating: false });
      }
    },
    [
      formatISODateTime,
      invalidateBooking,
      invalidatePotentialWarnings,
      invalidateProfileWithBooking,
      refreshBookingData,
      setBookingDragAndDropData,
      showGrowl,
      syncRows,
      t
    ]
  );
};
